import { useEffect, useState } from 'react';

import superagent from '@ott/superagent';
import { Activity } from '@ott/activity-card';

import { RequireOnlyOne } from '../types';
import trackKm from '../helpers/trackKm';
import { Meta, prepareMeta } from './helpers/prepareMeta';
import { prepareActivities, PrepareActivitiesOptions } from './helpers/prepareActivities';

export type SearchParams = RequireOnlyOne<{
  localityId: number;
  geonamesId: number;
  iata: string;
}> & {
  categories?: number | string; // возможна передача списка через запятую
  limit?: number;
  noCache?: boolean; // только для отладки
  similarTo?: number;
  startDate?: string; // YYYYMMDD
  endDate?: string;
  sortBy?: 'rating' | 'price';
};

type AdditionalParams = {
  trackId?: string;
  trackPage: string;
} & PrepareActivitiesOptions;

// пока только избранное
type UpdateActivity = (
  activityId: number,
  params: {
    key: 'favorite';
    value: boolean;
  }
) => void;

export type SearchResult = {
  activities: Activity[];
  hasError: boolean;
  isLoading: boolean;
  meta: Meta;
  updateActivity: UpdateActivity;
};

const useActivitiesSearch = (
  {
    categories,
    endDate,
    geonamesId,
    iata,
    limit = 5,
    localityId,
    noCache = false,
    similarTo,
    sortBy = 'rating',
    startDate,
  }: SearchParams,
  { trackId, trackPage, shouldPrioritiseDiscounted, shouldPrioritiseLiked }: AdditionalParams
): SearchResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [result, setResult] = useState({
    activities: [] as Activity[],
    meta: {} as Meta,
  });

  const getFirstAvailableId = () => {
    switch (true) {
      case Boolean(localityId):
        return { locality_id: localityId };
      case Boolean(geonamesId):
        return { geonames_id: geonamesId };
      case Boolean(iata):
        return { iata };
      default:
        return null;
    }
  };

  const getSearchQuery = () => {
    return {
      bot: true, // чтобы поиски виджета не учитывались в статистике
      categories,
      limit,
      no_cache: noCache,
      similar_to: similarTo,
      start_date: startDate,
      end_date: endDate,
      sort_by: sortBy,
      ...getFirstAvailableId(),
    };
  };

  const handleError = (error?: any) => {
    if (error) {
      console.error(error);
    }

    setHasError(true);
  };

  const handleSearchResponse = ({ body }: any) => {
    if (body.data?.length > 0) {
      const activities = prepareActivities(body.data, {
        shouldPrioritiseLiked,
        shouldPrioritiseDiscounted,
      });

      const meta = prepareMeta(body.meta, activities.length);

      setResult({
        activities,
        meta,
      });

      trackKm.onSearchSuccess({
        count: activities.length,
        localityId: meta.localityId,
        page: trackPage,
        id: trackId,
      });
    } else {
      handleError();
    }
  };

  useEffect(() => {
    superagent
      .get('/_activities/v3/search')
      .query(getSearchQuery())
      .then(handleSearchResponse)
      .catch(handleError)
      .finally(() => setIsLoading(false));
  }, []);

  const updateActivity: UpdateActivity = (activityId, { key, value }) => {
    const activities = result.activities.map((activity) => {
      if (activity.activityId === activityId) {
        return {
          ...activity,
          [key]: value,
        };
      }

      return activity;
    });

    setResult({
      ...result,
      activities,
    });
  };

  return {
    isLoading,
    hasError,
    activities: result.activities,
    meta: result.meta,
    updateActivity,
  };
};

export default useActivitiesSearch;
